import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  Bell,
  User,
  Settings,
  LogOut,
  Menu,
  LayoutDashboard,
  TrendingUp,
  DollarSign,
  Calculator,
  Users as UsersIcon,
  Package,
  Archive,
  Smartphone,
  Database,
  Layers,
  ScrollText,
  Gavel,
  Activity
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import IconButton from './IconButton';

const Header = ({ onToggleSidebar, sidebarOpen }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const profileMenuRef = useRef(null);
  const notificationsRef = useRef(null);
  const searchRef = useRef(null);

  // Lista de aplicações disponíveis
  const applications = [
    {
      id: 1,
      name: 'Análise Seguros',
      description: 'Sistema de Análise de Seguros',
      path: '/',
      icon: LayoutDashboard,
      keywords: ['dashboard', 'inicio', 'home', 'principal', 'visao geral']
    },
    
    {
      id: 17,
      name: 'Faixas',
      description: 'Gerenciamento de faixas de conversão',
      path: '/faixas',
      icon: Layers,
      keywords: ['faixas', 'conversao', 'ranges']
    },
    {
      id: 20,
      name: 'Regras',
      description: 'Gerenciamento de regras e taxas',
      path: '/regras',
      icon: Gavel,
      keywords: ['regras', 'taxas', 'rules']
    },
    {
      id: 21,
      name: 'Movimentação',
      description: 'Gerenciamento de movimentações',
      path: '/movimentacao',
      icon: Activity,
      keywords: ['movimentacao', 'movement', 'transacoes']
    },
    {
      id: 18,
      name: 'Convenção',
      description: 'Gerenciamento de convenções',
      path: '/convencao',
      icon: ScrollText,
      keywords: ['convencao', 'convenção', 'conv']
    },
    {
      id: 19,
      name: 'Cliente',
      description: 'Gerenciamento de clientes',
      path: '/cliente',
      icon: User,
      keywords: ['cliente', 'clientes']
    },
    {
      id: 7,
      name: 'Lançamentos',
      description: 'Movimentações de estoque (entradas/saídas)',
      path: '/lancamentos',
      icon: TrendingUp,
      keywords: ['lancamentos', 'movimentacoes', 'entradas', 'saidas', 'transacoes']
    },
    

    
    
    
    {
      id: 5,
      name: 'Usuários',
      description: 'Gerenciamento de usuários do sistema',
      path: '/usuarios',
      icon: UsersIcon,
      keywords: ['usuarios', 'users', 'pessoas', 'gerenciar', 'admin'],
      adminOnly: true
    },
    
    {
      id: 14,
      name: 'Configurações',
      description: 'Preferências e configurações do sistema',
      path: '/configuracoes',
      icon: Settings,
      keywords: ['configuracoes', 'ajustes', 'preferencias', 'settings']
    },
    {
      id: 6,
      name: 'Perfil',
      description: 'Meu perfil e configurações pessoais',
      path: '/profile',
      icon: User,
      keywords: ['perfil', 'profile', 'conta', 'configuracoes', 'dados pessoais']
    }
  ];

  // Fechar menus ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filtrar aplicações baseado na busca
  const filteredApplications = applications.filter(app => {
    // Filtrar por permissão de admin
    if (app.adminOnly && user?.role !== 'admin') {
      return false;
    }
    
    if (!searchQuery.trim()) return false;
    
    const query = searchQuery.toLowerCase();
    return (
      app.name.toLowerCase().includes(query) ||
      app.description.toLowerCase().includes(query) ||
      app.keywords.some(keyword => keyword.includes(query))
    );
  });

  const handleSearch = (e) => {
    e.preventDefault();
    if (filteredApplications.length === 1) {
      // Se há apenas um resultado, navegar diretamente
      navigate(filteredApplications[0].path);
      setSearchQuery('');
      setShowSearchResults(false);
    }
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowSearchResults(value.trim().length > 0);
  };

  const handleApplicationClick = (path) => {
    navigate(path);
    setSearchQuery('');
    setShowSearchResults(false);
  };

  const handleLogout = () => {
    logout();
    setShowProfileMenu(false);
  };

  // Notificações mockadas
  const notifications = [
    {
      id: 1,
      title: 'Nova venda registrada',
      message: 'Venda de R$ 1.500,00 foi registrada',
      time: '5 min atrás',
      unread: true
    },
    {
      id: 2,
      title: 'Relatório mensal',
      message: 'Relatório de vendas de outubro disponível',
      time: '1 hora atrás',
      unread: true
    },
    {
      id: 3,
      title: 'Backup concluído',
      message: 'Backup automático realizado com sucesso',
      time: '2 horas atrás',
      unread: false
    }
  ];

  const unreadCount = notifications.filter(n => n.unread).length;

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 px-6 py-[11.7px] transition-colors duration-200">
      <div className="flex items-center justify-between">
        {/* Left side */}
        <div className="flex items-center space-x-4">
          {/* Mobile menu button */}
          <IconButton
            icon={Menu}
            onClick={onToggleSidebar}
            variant="secondary"
            size="sm"
            title="Abrir menu"
            className="lg:hidden"
          />

          {/* Search */}
          <div className="relative" ref={searchRef}>
            <form onSubmit={handleSearch} className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder="Buscar aplicações..."
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  className="pl-9 pr-3 py-1.5 w-64 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-sm"
                />
              </div>
            </form>

            {/* Search Results Dropdown */}
            {showSearchResults && filteredApplications.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 max-h-64 overflow-y-auto">
                {filteredApplications.map((app) => {
                  const IconComponent = app.icon;
                  return (
                    <button
                      key={app.id}
                      onClick={() => handleApplicationClick(app.path)}
                      className="w-full flex items-center px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 text-left border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <IconComponent className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {app.name}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* No Results Message */}
            {showSearchResults && searchQuery.trim() && filteredApplications.length === 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400 text-sm">
                  Nenhuma aplicação encontrada para "{searchQuery}"
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <div className="relative" ref={notificationsRef}>
            <div className="relative inline-flex">
              <IconButton
                icon={Bell}
                onClick={() => setShowNotifications(!showNotifications)}
                variant="secondary"
                size="sm"
                title="Notificações"
              />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </div>

            {/* Notifications dropdown */}
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-semibold text-gray-900 dark:text-white">Notificações</h3>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 ${
                        notification.unread ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                            {notification.title}
                          </h4>
                          <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                            {notification.message}
                          </p>
                          <p className="text-gray-400 dark:text-gray-500 text-xs mt-2">
                            {notification.time}
                          </p>
                        </div>
                        {notification.unread && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-3 text-center border-t border-gray-200 dark:border-gray-700">
                  <button className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium">
                    Ver todas as notificações
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Profile menu */}
          <div className="relative" ref={profileMenuRef}>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="flex items-center p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                <span className="text-gray-600 dark:text-gray-300 font-medium text-sm">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
            </button>

            {/* Profile dropdown */}
            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                <div className="py-1">
                  <Link
                    to="/perfil"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <User className="w-4 h-4 mr-3" />
                    Meu Perfil
                  </Link>
                  <Link
                    to="/configuracoes"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <Settings className="w-4 h-4 mr-3" />
                    Configurações
                  </Link>
                  <hr className="my-1 border-gray-200 dark:border-gray-700" />
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Sair
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
